import axios, { AxiosInstance } from 'axios'
import { IApi } from '@/contracts/core/api/interface/IApi'
import { IRoute } from '@/contracts/core/api/interface/IRoute'
import { ApiResponse } from '@/contracts/core/api/entity/ApiResponse'
import { ApiBytesResponse } from '@/contracts/core/api/entity/ApiBytesResponse'
import { EnumRouteType } from '@/contracts/core/api/enum/EnumRouteType'
import { EnumApiError } from '@/contracts/core/api/enum/EnumApiError'
import { writeToken } from '@/helpers/tokenStorage'
import { isArrayBuffer } from '@/helpers/isArrayBuffer'

const TOKEN_HEADER = 'x-auth-token'
const CONTENT_RANGE_HEADER = 'content-range'
const CONTENT_LENGTH_HEADER = 'content-length'

interface IExchangeBody {
  success: boolean
  data?: unknown
  error?: string | null
}

export class ApiV1 implements IApi {
  private token: string | null = null
  private readonly http: AxiosInstance

  constructor(http?: AxiosInstance) {
    this.http = http ?? axios.create({
      baseURL: process.env.API_URL || '',
      // тело в стандарте обмена разбирается при любом коде HTTP
      validateStatus: () => true,
    })
  }

  setToken(token: string | null): void {
    this.token = token
  }

  async exec<P, T>(route: IRoute<P, T>, params: P): Promise<ApiResponse<T>> {
    try {
      const response = await this.http.request({
        url: route.url(),
        method: route.type(),
        params: route.type() === EnumRouteType.GET ? params : undefined,
        data: route.type() === EnumRouteType.GET ? undefined : params,
        headers: this._headers(),
      })
      this._readNewToken(response.headers[TOKEN_HEADER])

      return this._toApiResponse<T>(response.data, response.headers)
    } catch {
      return new ApiResponse<T>({ success: false, error: EnumApiError.NETWORK_ERROR })
    }
  }

  async sendBytes<P, T>(route: IRoute<P, T>, params: P, body: Blob, headers: Record<string, string>): Promise<ApiResponse<T>> {
    void params
    try {
      const response = await this.http.request({
        url: route.url(),
        method: route.type(),
        data: body,
        headers: { ...this._headers(), 'Content-Type': 'application/octet-stream', ...headers },
      })
      this._readNewToken(response.headers[TOKEN_HEADER])

      return this._toApiResponse<T>(response.data)
    } catch {
      return new ApiResponse<T>({ success: false, error: EnumApiError.NETWORK_ERROR })
    }
  }

  async getBytes<P>(route: IRoute<P, unknown>, params: P, headers: Record<string, string>): Promise<ApiBytesResponse> {
    void params
    try {
      const response = await this.http.request({
        url: route.url(),
        method: route.type(),
        responseType: 'arraybuffer',
        headers: { ...this._headers(), ...headers },
      })
      this._readNewToken(response.headers[TOKEN_HEADER])

      return this._toBytesResponse(response.status, response.data, response.headers)
    } catch {
      return new ApiBytesResponse({ success: false, error: EnumApiError.NETWORK_ERROR })
    }
  }

  _headers(): Record<string, string> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (this.token) {
      headers.Authorization = 'Bearer ' + this.token
    }

    return headers
  }

  /** Продлённый токен из заголовка ответа заменяет сохранённый */
  _readNewToken(header: unknown): void {
    if (typeof header === 'string' && header !== '' && header !== this.token) {
      this.token = header
      writeToken(header)
    }
  }

  _toApiResponse<T>(body: unknown, headers: unknown = {}): ApiResponse<T> {
    if (!this._isExchangeBody(body)) {
      return new ApiResponse<T>({ success: false, error: EnumApiError.BAD_RESPONSE })
    }

    return new ApiResponse<T>({
      success: body.success,
      data: body.success ? body.data as T : undefined,
      error: body.error ?? undefined,
      headers: this._stringHeaders(headers),
    })
  }

  /** Заголовки axios — объект со строками, массивами и служебными полями: нужны только строки */
  _stringHeaders(headers: unknown): Record<string, string> {
    const result: Record<string, string> = {}
    if (typeof headers !== 'object' || headers === null) {
      return result
    }
    for (const [name, value] of Object.entries(headers as Record<string, unknown>)) {
      if (typeof value === 'string') {
        result[name] = value
      }
    }

    return result
  }

  /** 200 и 206 — байты; размер файла — из Content-Range (bytes a-b/size), без него — длина тела */
  _toBytesResponse(status: number, data: unknown, headers: Record<string, unknown>): ApiBytesResponse {
    if (!isArrayBuffer(data)) {
      return new ApiBytesResponse({ success: false, error: EnumApiError.BAD_RESPONSE })
    }
    if (status === 200 || status === 206) {
      return new ApiBytesResponse({ success: true, data, size: this._fileSize(headers, data.byteLength) })
    }
    let body: unknown
    try {
      body = JSON.parse(new TextDecoder().decode(data))
    } catch {
      return new ApiBytesResponse({ success: false, error: EnumApiError.BAD_RESPONSE })
    }
    const response = this._toApiResponse<unknown>(body)

    return new ApiBytesResponse({ success: false, error: response.isSuccess() ? EnumApiError.BAD_RESPONSE : response.getError() })
  }

  _fileSize(headers: Record<string, unknown>, bodyLength: number): number {
    const range = /^bytes \d+-\d+\/(\d+)$/.exec(String(headers[CONTENT_RANGE_HEADER] ?? ''))
    if (range !== null) {
      return Number(range[1])
    }
    const length = Number(headers[CONTENT_LENGTH_HEADER])

    return Number.isFinite(length) && length > 0 ? length : bodyLength
  }

  _isExchangeBody(body: unknown): body is IExchangeBody {
    return typeof body === 'object' && body !== null && typeof (body as IExchangeBody).success === 'boolean'
  }
}
